// pages/StaffProfile.jsx - Staff Profile View Only (Responsive, Professional Redesign)
import React, { useState, useEffect } from 'react';
import {
    User, Mail, Phone, Shield, Calendar, Building2,
    BadgeCheck, MapPin, UserCog, Clock, AlertCircle,
    Loader2, RefreshCw, CheckCircle, Cog, Lock, Hash
} from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = 'https://api.mobilink.tech/api/superAdmin/staff';

// ── Shared Panel Component ─────────────────────────────────────────────
const Panel = ({ children, className = "" }) => (
    <div
        className={`rounded-2xl bg-white ${className}`}
        style={{
            border: '1px solid rgba(255,125,56,0.15)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
        }}
    >
        {children}
    </div>
);

// ── Panel Section Header ────────────────────────────────────────────────
const PanelHeader = ({ icon: Icon, title, subtitle }) => (
    <div className="flex items-center gap-3 px-5 sm:px-6 pt-5 pb-4 border-b border-orange-100">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-md shrink-0">
            <Icon size={16} className="text-white" />
        </div>
        <div className="min-w-0">
            <h2 className="font-semibold text-gray-800 text-sm sm:text-[15px] truncate">{title}</h2>
            <p className="text-xs text-gray-500 truncate">{subtitle}</p>
        </div>
    </div>
);

// ── Shared Message Component ───────────────────────────────────────────
const Message = ({ type, message }) => {
    if (!message) return null;

    const isError = type === "error";
    const colors = isError
        ? {
            color: "#dc2626",
            backgroundColor: "rgba(239,68,68,0.08)",
            borderColor: "rgba(239,68,68,0.2)",
        }
        : {
            color: "#16a34a",
            backgroundColor: "rgba(34,197,94,0.08)",
            borderColor: "rgba(34,197,94,0.2)",
        };

    return (
        <div
            className="flex items-center gap-2 text-sm rounded-xl px-4 py-3 border animate-[fadeIn_0.2s_ease-out]"
            style={colors}
        >
            {isError ? (
                <AlertCircle size={16} className="shrink-0" />
            ) : (
                <CheckCircle size={16} className="shrink-0" />
            )}
            <span className="leading-snug">{message}</span>
        </div>
    );
};

// ── Read-Only Field Component ──────────────────────────────────────────
const ReadOnlyField = ({ icon: Icon, label, value, subValue }) => (
    <div>
        <label className="block text-xs font-medium mb-1.5 text-gray-600">{label}</label>
        <div className="relative group">
            <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-hover:text-orange-500 transition-colors" />
            <div
                className="w-full pl-9 pr-3.5 py-2.5 text-sm rounded-xl bg-orange-50/70 border border-orange-200 text-gray-800 cursor-default select-all min-h-[42px] flex items-center transition-colors group-hover:bg-orange-50 group-hover:border-orange-300"
                title={value || 'Not provided'}
            >
                <span className="truncate">{value || 'Not provided'}</span>
            </div>
        </div>
        {/* {subValue && (
            <p className="text-[10px] mt-1 text-gray-400 pl-1">{subValue}</p>
        )} */}
    </div>
);

// ── Stat Card Component ─────────────────────────────────────────────────
const StatCard = ({ icon: Icon, value, label }) => (
    <div className="p-3 sm:p-3.5 rounded-xl bg-orange-50/70 border border-orange-200 text-center transition-colors hover:bg-orange-50">
        <Icon size={16} className="mx-auto mb-1.5 text-orange-500" />
        <p className="text-xs sm:text-sm font-bold text-gray-800 truncate">{value || '—'}</p>
        <p className="text-[10px] text-gray-500 mt-0.5">{label}</p>
    </div>
);

// ── Main Staff Profile Component ───────────────────────────────────────
export const StaffProfile = () => {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [message, setMessage] = useState({ type: '', message: '' });

    const getToken = () => sessionStorage.getItem('subAdminToken') || '';

    useEffect(() => {
        fetchStaffProfile();
    }, []);

    const fetchStaffProfile = async (isRefresh = false) => {
        const token = getToken();

        if (!token) {
            setMessage({ type: 'error', message: 'No authentication token found. Please login again.' });
            setLoading(false);
            return;
        }

        if (isRefresh) setRefreshing(true);
        else setLoading(true);

        setMessage({ type: '', message: '' });

        try {
            const response = await axios.post(
                `${API_BASE_URL}/getprofile`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                const staff = response.data.staff;
                const profileData = {
                    id: staff._id,
                    name: staff.name,
                    email: staff.email,
                    phone: staff.phone,
                    address: staff.address,
                    role: staff.roleName || staff.role,
                    roleId: staff.roleId,
                    department: staff.departmentName || staff.department,
                    departmentId: staff.departmentId,
                    image: staff.image,
                    status: staff.status,
                    createdAt: staff.createdAt,
                    updatedAt: staff.updatedAt
                };

                setProfile(profileData);

                // Sync session storage
                sessionStorage.setItem('subAdminData', JSON.stringify(profileData));
                sessionStorage.setItem('staffName', staff.name);
                sessionStorage.setItem('staffEmail', staff.email);
                sessionStorage.setItem('staffRole', staff.roleName || staff.role);
                sessionStorage.setItem('staffDepartment', staff.departmentName || staff.department);
                sessionStorage.setItem('staffPhone', staff.phone || '');
                sessionStorage.setItem('staffImage', staff.image || '');

                setMessage({
                    type: 'success',
                    message: isRefresh ? 'Profile refreshed successfully' : 'Profile loaded successfully'
                });
                setTimeout(() => setMessage({ type: '', message: '' }), 3000);
            } else {
                setMessage({ type: 'error', message: response.data.message || 'Failed to load profile' });
            }
        } catch (err) {
            console.error('Error fetching profile:', err);
            if (err.response?.status === 401) {
                setMessage({ type: 'error', message: 'Session expired. Please login again.' });
            } else if (err.response?.status === 404) {
                setMessage({ type: 'error', message: 'Staff profile not found.' });
            } else {
                setMessage({ type: 'error', message: err.response?.data?.message || 'Failed to load profile' });
            }
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric', month: 'long', day: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'active': return '#22c55e';
            case 'inactive': return '#ef4444';
            case 'pending': return '#f59e0b';
            default: return '#6b7280';
        }
    };

    const getStatusLabel = (status) => {
        switch (status?.toLowerCase()) {
            case 'active': return 'Active';
            case 'inactive': return 'Inactive';
            case 'pending': return 'Pending';
            default: return status || 'Unknown';
        }
    };

    if (loading && !profile) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
                <div className="text-center">
                    <Loader2 size={40} className="animate-spin mx-auto" style={{ color: '#FF7D38' }} />
                    <p className="mt-4 text-gray-600 text-sm">Loading profile...</p>
                </div>
            </div>
        );
    }

    const initials = profile?.name?.split(" ").filter(Boolean).map(w => w[0]).join("").slice(0, 2).toUpperCase() || "ST";

    return (
        <div className="bg-gray-50 min-h-screen p-3 sm:p-4 lg:p-6 rounded-2xl">
            <div className="max-w-6xl mx-auto space-y-5">

                {/* ── Hero / Cover Header ─────────────────────────────────── */}
                <Panel className="overflow-hidden">
                    {/* Cover gradient */}
                    <div className="h-24 sm:h-28 md:h-32 relative overflow-hidden rounded-t-2xl">
                        <svg width="100%" height="100%" viewBox="0 0 680 150" preserveAspectRatio="xMidYMid slice" className="absolute inset-0">
                            <rect x="0" y="0" width="680" height="150" fill="#fbd9b0" />
                            <circle cx="330" cy="32" r="16" fill="#f5b869" />
                            <path d="M410 75 L 330 32" stroke="#f9c98a" strokeWidth="7" opacity="0.35" />
                            <path d="M450 85 L 330 32" stroke="#f9c98a" strokeWidth="5" opacity="0.3" />
                            <path d="M0,95 C110,80 220,105 340,90 C460,75 560,98 680,85 L680,150 L0,150 Z" fill="#e8894a" opacity="0.5" />
                            <path d="M0,112 C120,98 240,118 370,105 C500,92 590,112 680,102 L680,150 L0,150 Z" fill="#df7a3d" />
                            <g>
                                <rect x="72" y="72" width="4" height="48" fill="#2b1c14" />
                                <path d="M74 78 L 60 96 M74 86 L 86 100 M74 70 L 64 84" stroke="#2b1c14" strokeWidth="2.5" fill="none" strokeLinecap="round" />
                                <ellipse cx="74" cy="60" rx="36" ry="30" fill="#e2712f" />
                                <ellipse cx="52" cy="68" rx="20" ry="17" fill="#e2712f" />
                            </g>
                            <g>
                                <rect x="248" y="105" width="3" height="22" fill="#2b1c14" />
                                <ellipse cx="249" cy="98" rx="11" ry="13" fill="#f0954c" />
                            </g>
                            <g>
                                <rect x="278" y="98" width="3" height="30" fill="#2b1c14" />
                                <ellipse cx="280" cy="88" rx="12" ry="19" fill="#e2712f" />
                            </g>
                            <g>
                                <rect x="308" y="100" width="3" height="28" fill="#2b1c14" />
                                <ellipse cx="309" cy="92" rx="10" ry="16" fill="#f0954c" />
                            </g>
                            <g>
                                <rect x="530" y="78" width="4" height="45" fill="#2b1c14" />
                                <path d="M532 84 L 520 98 M532 92 L 545 104" stroke="#2b1c14" strokeWidth="2.5" fill="none" strokeLinecap="round" />
                                <ellipse cx="533" cy="66" rx="32" ry="27" fill="#df6f2c" />
                                <ellipse cx="557" cy="74" rx="17" ry="14" fill="#df6f2c" />
                            </g>
                            <g>
                                <rect x="608" y="108" width="3" height="20" fill="#2b1c14" />
                                <ellipse cx="609" cy="102" rx="10" ry="12" fill="#c65f2a" />
                            </g>
                            <path d="M0,128 C130,120 260,132 390,124 C520,116 590,130 680,122 L680,150 L0,150 Z" fill="#c65f2a" />
                            <g stroke="#8a3f1c" strokeWidth="1.5" strokeLinecap="round">
                                <path d="M14 140 L 10 131" /><path d="M20 140 L 22 130" /><path d="M26 141 L 29 132" />
                                <path d="M650 141 L 646 130" /><path d="M658 141 L 661 131" /><path d="M666 140 L 663 130" />
                            </g>
                        </svg>

                        {/* Soft overlay for text/button legibility */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent" />

                        {/* Refresh button */}
                        <div className="absolute top-3 right-3 sm:top-4 sm:right-4">
                            <button
                                onClick={() => fetchStaffProfile(true)}
                                disabled={refreshing}
                                className="flex items-center justify-center gap-2 text-xs font-medium px-3 sm:px-3.5 py-2 rounded-xl transition-all bg-white/90 backdrop-blur text-orange-600 hover:bg-white disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
                            >
                                {refreshing ? (
                                    <Loader2 size={14} className="animate-spin" />
                                ) : (
                                    <RefreshCw size={14} />
                                )}
                                <span className="hidden xs:inline">Refresh</span>
                            </button>
                        </div>
                    </div>

                    {/* Identity strip */}
                    <div className="px-4 sm:px-6 pb-5 sm:pb-6">
                        <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-5">

                            {/* Avatar */}
                            <div className="relative shrink-0 -mt-10 sm:-mt-12 mx-auto md:mx-0">
                                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white text-xl sm:text-2xl font-bold shadow-lg overflow-hidden ring-4 ring-white">
                                    {profile?.image ? (
                                        <img src={profile.image} alt={profile.name} className="w-full h-full object-cover" />
                                    ) : (
                                        initials
                                    )}
                                </div>
                                <div
                                    className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-[3px] border-white flex items-center justify-center"
                                    style={{ background: getStatusColor(profile?.status) }}
                                    title={getStatusLabel(profile?.status)}
                                >
                                    {profile?.status?.toLowerCase() === 'active' && (
                                        <CheckCircle size={11} className="text-white" />
                                    )}
                                </div>
                            </div>

                            {/* Name + meta */}
                            <div className="flex-1 min-w-0 text-center md:text-left pt-1 md:pt-3">
                                <h1 className="text-lg sm:text-xl font-bold text-gray-800 truncate">
                                    {profile?.name || "Staff Member"}
                                </h1>
                                <p className="text-sm text-gray-500 truncate mt-0.5">{profile?.email}</p>

                                <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mt-3">
                                    <span
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border whitespace-nowrap"
                                        style={{
                                            background: `${getStatusColor(profile?.status)}15`,
                                            color: getStatusColor(profile?.status),
                                            borderColor: `${getStatusColor(profile?.status)}30`
                                        }}
                                    >
                                        <span
                                            className="w-1.5 h-1.5 rounded-full shrink-0"
                                            style={{ background: getStatusColor(profile?.status) }}
                                        />
                                        {getStatusLabel(profile?.status)}
                                    </span>
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-orange-50 text-orange-600 border border-orange-200 whitespace-nowrap">
                                        <UserCog size={11} className="shrink-0" />
                                        <span className="truncate max-w-[120px]">{profile?.role || 'Staff'}</span>
                                    </span>
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-orange-50 text-orange-600 border border-orange-200 whitespace-nowrap">
                                        <Building2 size={11} className="shrink-0" />
                                        <span className="truncate max-w-[120px]">{profile?.department || '—'}</span>
                                    </span>
                                </div>
                            </div>

                            {/* Role badge (desktop only) */}
                            <div className="hidden lg:flex items-center gap-2 text-xs px-3.5 py-2 rounded-xl bg-orange-50 text-orange-500 border border-orange-200 shrink-0 self-start mt-3 md:mt-0">
                                <Cog size={16} />
                                <span className="font-medium whitespace-nowrap">Staff Account</span>
                            </div>
                        </div>
                    </div>
                </Panel>

                {/* Global Message */}
                {/* <Message type={message.type} message={message.message} /> */}

                {/* ── Content Grid ─────────────────────────────────────────── */}
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-5 items-start">

                    {/* LEFT COLUMN: Stats */}
                    <div className="lg:col-span-2">
                        <Panel>
                            <PanelHeader icon={User} title="Overview" subtitle="Key account details at a glance" />
                            <div className="p-5 sm:p-6">
                                <div className="grid grid-cols-1 gap-3">
                                    {/* <StatCard icon={Hash} value={profile?.id} label="Staff ID" /> */}
                                    <StatCard icon={UserCog} value={profile?.role} label="Role" />
                                    <StatCard icon={Building2} value={profile?.department} label="Department" />                                    
                                    <StatCard icon={BadgeCheck} value={getStatusLabel(profile?.status)} label="Status" />
                                </div>
                            </div>
                        </Panel>
                    </div>

                    {/* RIGHT COLUMN: Details & Account Info */}
                    <div className="lg:col-span-3 space-y-4 sm:space-y-5">
                        {/* Personal Information */}
                        <Panel>
                            <PanelHeader icon={Mail} title="Personal Information" subtitle="Contact and department details" />
                            <div className="p-5 sm:p-6 space-y-4">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <ReadOnlyField icon={User} label="Full Name" value={profile?.name} />
                                    <ReadOnlyField icon={Mail} label="Email Address" value={profile?.email} />
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <ReadOnlyField icon={Phone} label="Phone Number" value={profile?.phone} />
                                    <ReadOnlyField icon={MapPin} label="Address" value={profile?.address} />
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <ReadOnlyField
                                        icon={Building2}
                                        label="Department"
                                        value={profile?.department}
                                        subValue={`ID: ${profile?.departmentId?.slice(-6) || '—'}`}
                                    />
                                    <ReadOnlyField
                                        icon={Shield}
                                        label="Role"
                                        value={profile?.role}
                                        subValue={`ID: ${profile?.roleId?.slice(-6) || '—'}`}
                                    />
                                </div>
                            </div>
                        </Panel>

                        {/* Account Information */}
                        <Panel>
                            <PanelHeader icon={Clock} title="Account Information" subtitle="Timestamps and verification" />
                            <div className="p-5 sm:p-6 space-y-4">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <ReadOnlyField icon={Calendar} label="Account Created" value={formatDate(profile?.createdAt)} />
                                    <ReadOnlyField icon={Clock} label="Last Updated" value={formatDate(profile?.updatedAt)} />
                                </div>
                                <ReadOnlyField
                                    icon={BadgeCheck}
                                    label="Verification Status"
                                    value={profile?.status === 'active' ? '✅ Verified Account' : '⚠️ Inactive / Pending'}
                                />
                            </div>
                        </Panel>

                        {/* Read-only Notice Banner */}
                        <div className="p-4 sm:p-5 rounded-2xl bg-blue-50 border border-blue-200 flex items-start gap-3">
                            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                                <Lock size={15} className="text-blue-500" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-blue-700">Read-Only View</p>
                                <p className="text-xs text-blue-600 mt-0.5 leading-relaxed">
                                    This profile is managed by administration. Contact your supervisor for any corrections or updates.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StaffProfile;